/**
 * 退票人工複核工作清單（M1 S5 / AC-E2-5 24h SLA / AC-E2-6）。
 *
 * 對應 BE GET /admin/refunds/manual-review（filter / sort / page）。
 * 點列進 detail 頁可執行督導決議（核准完成 / 取消退票）。
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../components/Button';
import { Pill } from '../components/Pill';
import { listManualReviews, type ManualReviewItem } from '../lib/api-client';

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

/** 逾時標示：overdue_minutes > 0 → 紅；否則綠。 */
function OverdueBadge({ minutes }: { minutes: number }): JSX.Element {
  if (minutes > 0) {
    const h = Math.floor(minutes / 60);
    const label = h > 0 ? `逾時 ${h}h${minutes % 60}m` : `逾時 ${minutes}m`;
    return <Pill tone="danger">{label}</Pill>;
  }
  return <Pill tone="success">SLA 內</Pill>;
}

export function ManualReviewListPage(): JSX.Element {
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['admin.refunds.manual-review', overdueOnly],
    queryFn: () => listManualReviews({ overdue: overdueOnly, sort: 'due_at', order: 'asc' }),
  });

  const items: ManualReviewItem[] = data?.items ?? [];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">退票人工複核（AC-E2-6）</h1>
        <div className="page-head__actions" style={{ alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={overdueOnly}
              onChange={(e) => setOverdueOnly(e.target.checked)}
            />
            只看逾時
          </label>
          <Button variant="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? '更新中…' : '重新整理'}
          </Button>
        </div>
      </div>

      <p className="page-intro">
        24 h SLA（AC-E2-5）；逾時由 sweeper 自動升級。點選一列進入細節頁執行督導決議（核准完成 /
        取消退票）。
      </p>

      {isLoading && <div>載入中…</div>}
      {error && (
        <div className="error-banner">
          {(error as Error).message}
          <div style={{ marginTop: 4, fontSize: 12 }}>
            提醒：此頁需 superadmin token + admin.audit_log 權限。
          </div>
        </div>
      )}
      {data && (
        <>
          <div className="table-card">
            <table>
              <thead>
                <tr>
                  <th>退款 ID</th>
                  <th>訂單</th>
                  <th>金額</th>
                  <th>原因</th>
                  <th>重試</th>
                  <th>SLA 期限</th>
                  <th>狀態</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty-cell">
                      {overdueOnly ? '目前無逾時項目' : '目前無待複核項目'}
                    </td>
                  </tr>
                )}
                {items.map((r) => (
                  <tr key={r.refund_id}>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      <Link to={`/refunds/manual-reviews/${r.refund_id}`}>
                        {r.refund_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {r.order_id.slice(0, 8)}…
                    </td>
                    <td>NT$ {Number(r.refund_amount).toLocaleString()}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.reason ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{r.retry_count}</td>
                    <td style={{ fontSize: 12, color: 'var(--c-text-muted)' }}>
                      {formatDateTime(r.manual_review_due_at)}
                    </td>
                    <td>
                      <OverdueBadge minutes={r.overdue_minutes} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="list-foot">
            <div>
              共 {data.total} 筆（第 {data.page} 頁，每頁 {data.limit}）
            </div>
          </div>
        </>
      )}
    </div>
  );
}
